import type { WorkflowTrace } from '@toolproof-core/genesis/_contract/_index';
import type { ScenePlan, PlanEffect } from '@toolproof-core/lib/_contract/scene-plan';
import type { WorkflowTraceTarget } from './_contract/episode';

import { validateScenePlanReplay } from '@/_pure/episode';
import { loadEpisodeCatalog } from '@/_pure/episodeCatalog';
import { resolveEpisodeExcerpt } from '@/_pure/episodeExcerpt';
import catalog from '@/workflow-runner/_data/episodes/_@shared/catalog.json' with { type: 'json' };
import branchStep from '@/workflow-runner/_data/episodes/branch-step.json' with { type: 'json' };
import resourceTypesAndResources from '@/workflow-runner/_data/episodes/resource-types-and-resources.json' with { type: 'json' };
import productSumTypes from '@/workflow-runner/_data/episodes/product-sum-types.json' with { type: 'json' };
import resourceTypeUniverses from '@/workflow-runner/_data/episodes/resource-type-universes.json' with { type: 'json' };
import toolsAndToolTypes from '@/workflow-runner/_data/episodes/tools-and-tool-types.json' with { type: 'json' };
import toolInvocations from '@/workflow-runner/_data/episodes/tool-invocations.json' with { type: 'json' };
import whileStep from '@/workflow-runner/_data/episodes/while-step.json' with { type: 'json' };
import forStep from '@/workflow-runner/_data/episodes/for-step.json' with { type: 'json' };
import operationPresentationMode from '@/workflow-runner/_data/episodes/operation-presentation-mode.json' with { type: 'json' };
import workflow from '@/workflow-runner/_data/episodes/workflow.json' with { type: 'json' };
import { parseWorkflowEpisode } from '@/workflow-runner/_pure/episode';
import { deriveWorkflowTraceReplay } from '@/workflow-runner/_pure/episodeReplay';
import { createTraceSnapshotResolver, toSortedWorkflowTraces } from '@/workflow-runner/model/traceSnapshot';
import { workflowVisualSources } from '@/workflow-runner/visualSourceCatalog';
import type { TraceReplay } from '@/_contract/trace';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { WorkflowEpisode } from '@/workflow-runner/_contract/episode';
import type { RuntimeState } from '@/workflow-runner/_contract/runtime';

const documents = new Map<string, unknown>([
    ['resource-types-and-resources', resourceTypesAndResources], ['product-sum-types', productSumTypes], ['resource-type-universes', resourceTypeUniverses],
    ['tools-and-tool-types', toolsAndToolTypes], ['tool-invocations', toolInvocations],
    ['workflow', workflow],
    ['while-step', whileStep], ['for-step', forStep], ['branch-step', branchStep],
    ['operation-presentation-mode', operationPresentationMode],
]);
export const workflowEpisodes = loadEpisodeCatalog('Workflow', catalog, documents,
    value => parseWorkflowEpisode(value, workflowVisualSources));
const replayByTrace = new WeakMap<WorkflowTrace, TraceReplay>();

export function getAvailableWorkflowEpisodes(cosmosData: CosmosDataModel): WorkflowEpisode[] {
    const handles = new Set<string>(toSortedWorkflowTraces(cosmosData).map((trace) => trace.handle));
    return workflowEpisodes.filter((episode) => episode.scenes.every((scene) =>
        scene.sceneKind === 'concept' || handles.has(scene.trace.handle)));
}
export function resolveWorkflowEpisode(handle: string, sceneIndex = 0) {
    const episode = workflowEpisodes.find((episode) => episode.handle === handle);
    const scene = episode?.scenes[sceneIndex];
    if (!episode || !scene) throw new Error(`Workflow Episode '${handle}' or its scene is unavailable.`);
    return { episode, scene };
}
export function resolveWorkflowBeatGrouping(runtimeState: RuntimeState) {
    if (!runtimeState.episode) return undefined;
    const { scene } = resolveWorkflowEpisode(runtimeState.episode.episodeHandle, runtimeState.sceneIndex ?? 0);
    const beat = scene.beats[runtimeState.episode.beatIndex];
    if (!beat) throw new Error('Workflow episode beat is unavailable.');
    return scene.sceneKind === 'trace' ? scene.beats[runtimeState.episode.beatIndex]!.grouping ?? 'none' : 'none';
}
export function resolveWorkflowScenePlan(cosmosData: CosmosDataModel, runtimeState: RuntimeState,
    handle: string, sceneIndex = runtimeState.sceneIndex ?? 0) {
    const { episode, scene } = resolveWorkflowEpisode(handle, sceneIndex);
    if (scene.sceneKind !== 'trace') throw new Error('Workflow concept scene has no replay trace.');
    const trace = toSortedWorkflowTraces(cosmosData, runtimeState.ephemeralWorkflowTraces).find((trace) => trace.handle === scene.trace.handle);
    if (!trace) throw new Error(`Workflow scene trace '${scene.trace.handle}' is unavailable.`);
    let replay = replayByTrace.get(trace);
    if (!replay) {
        const snapshot = createTraceSnapshotResolver().deriveTraceSnapshot({
            cosmosData, runtimeState: { ...runtimeState, conceptArrangement: undefined,
                traceSelection: { workflowTraceSelectionKind: 'by-handle', workflowTraceHandle: trace.handle, replayPositionIndex: 0 } },
        });
        if (!snapshot || snapshot.selectedTraceHandle !== trace.handle) throw new Error('Scene trace could not be resolved exactly.');
        replay = deriveWorkflowTraceReplay(snapshot);
        replayByTrace.set(trace, replay);
    }
    const plan: ScenePlan<PlanEffect<WorkflowTraceTarget>> = { schemaVersion: 1, title: scene.title, trace: scene.trace, beats: scene.beats };
    if (plan.beats.some(beat => beat.cues?.some(cue => cue.phase === 'start'))) {
        throw new Error('Workflow scenes do not yet support action-start cues.');
    }
    validateScenePlanReplay(plan, replay);
    const excerpt = resolveEpisodeExcerpt(scene.excerpt, plan, replay);
    return { episode, scene, plan, replay, trace, excerpt, openingStepIndex: excerpt?.from ?? 0 };
}